import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { GOAL_CARDS } from "@/data/homepageData";

/** Section 2 — Choose Your Goal: clicking a card personalizes the journey by
 * routing straight to the most relevant next step (register flow, or the
 * recruiter page for "Hire talent"). */
export const ChooseYourGoalSection = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const handleSelect = (goal) => {
    setSelected(goal.key);
    setTimeout(() => navigate(goal.route), 350);
  };

  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="choose-your-goal-section">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">What Brings You Here?</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Choose Your Goal</h2>
        <p className="text-slate-400 mt-3">We'll personalize your path the moment you pick one.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {GOAL_CARDS.map((g, i) => {
          const isSelected = selected === g.key;
          return (
            <motion.button
              key={g.key} onClick={() => handleSelect(g)} data-testid={`goal-card-${g.key}`}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
              whileHover={{ y: -6 }} whileTap={{ scale: 0.97 }}
              className="group relative flex flex-col items-start gap-4 p-6 rounded-3xl border overflow-hidden text-left transition-colors duration-300"
              style={{
                borderColor: isSelected ? `${g.color}80` : "rgba(255,255,255,0.1)",
                background: isSelected ? `linear-gradient(160deg, ${g.color}1f, rgba(255,255,255,0.02))` : "rgba(255,255,255,0.02)",
              }}
            >
              {/* Ambient glow that blooms in on hover, tinted to this goal's color */}
              <div
                className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 pointer-events-none"
                style={{ background: g.color }}
              />
              <div
                className="relative w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${g.color}1f`, border: `1px solid ${g.color}40`, boxShadow: `0 0 24px ${g.color}25` }}
              >
                <g.icon className="w-6 h-6" style={{ color: g.color }} strokeWidth={1.75} />
              </div>
              <div className="relative">
                <p className="text-base font-semibold text-white leading-snug">{g.title}</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{g.desc}</p>
              </div>
              <div className="relative flex items-center gap-1.5 text-xs font-semibold mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: g.color }}>
                Get started <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
};
